"use client";

import { CheckCircle2, ChevronRight, ShoppingBag } from "lucide-react";
import { BrandMark } from "@/components/branding/brand-mark";
import Link from "@/components/ui/link";
import { useNow } from "@/hooks/use-now";
import { brandThemeStyle } from "@/lib/branding/brand-theme";
import { formatPriceCompact as formatPrice } from "@/lib/currency";
import type {
  EventAddonMenu,
  SessionAddonState,
} from "@/lib/events/addon-types";
import { addonDeadlineRule } from "@/lib/events/addons";
import { cn } from "@/lib/utils";
import { AddonDeadline, AddonThumb } from "./addon-parts.client";
import { useEventAddons } from "./event-addons-provider.client";

/** The date the page is showing, else the viewer's ticketed one, else the next. */
function focusSession(
  menu: EventAddonMenu,
  previewOccurrenceId: number | null,
): SessionAddonState | null {
  return (
    menu.sessions.find(
      (session) => session.occurrenceId === previewOccurrenceId,
    ) ??
    menu.sessions.find((session) => session.bookingId) ??
    menu.sessions[0] ??
    null
  );
}

/** Items the viewer has paid for across every date of this event. */
function orderedItems(menu: EventAddonMenu) {
  let count = 0;
  let occurrenceId: number | null = null;
  for (const session of menu.sessions)
    for (const order of session.orders) {
      if (!["paid", "fulfilled"].includes(order.status)) continue;
      occurrenceId ??= session.occurrenceId;
      for (const pickup of order.pickups)
        for (const line of pickup.lines) count += line.quantity;
    }
  return { count, occurrenceId };
}

/**
 * The compact entry point under the event's date — the first thing on the
 * page that says there is more than a ticket here, and, once something is
 * ordered, that it is.
 */
export function EventAddonsPill() {
  const addons = useEventAddons();
  if (!addons) return null;
  const { menu } = addons;
  const single = menu.sections.length === 1 ? menu.sections[0] : null;
  const ordered = orderedItems(menu);
  return (
    <button
      type="button"
      onClick={() => addons.open()}
      className={cn(
        "inline-flex max-w-full items-center gap-2 rounded-full border py-1.5 pr-2.5 pl-2 text-left text-sm transition-colors",
        ordered.count
          ? "border-emerald-500/30 bg-emerald-500/10 active:bg-emerald-500/15"
          : "border-brand/20 bg-brand/8 active:bg-brand/15 dark:border-white/12 dark:bg-white/8",
      )}
    >
      <span
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-full text-white",
          ordered.count ? "bg-emerald-600" : "bg-brand",
        )}
      >
        {ordered.count ? (
          <CheckCircle2 className="size-3.5" aria-hidden />
        ) : (
          <ShoppingBag className="size-3.5" aria-hidden />
        )}
      </span>
      <span className="min-w-0 truncate">
        <span className="text-lg leading-snug font-bold text-text-primary dark:text-white">
          {ordered.count
            ? `You've pre-ordered ${ordered.count} item${ordered.count === 1 ? "" : "s"}`
            : single
              ? `Pre-order ${single.title.toLowerCase()}`
              : `Pre-order ${menu.sections
                  .map((section) => section.title.toLowerCase())
                  .join(" · ")}`}
        </span>
        {!ordered.count && single?.partnerName ? (
          <span className="text-text-secondary dark:text-white/60">
            {" "}
            · by {single.partnerName}
          </span>
        ) : null}
      </span>
      <ChevronRight
        className="size-4 shrink-0 text-text-tertiary dark:text-white/50"
        aria-hidden
      />
    </button>
  );
}

/**
 * The sections on the event's Details tab. Photos make a rail; a menu without
 * them reads as a short priced list instead — half-empty image tiles look
 * broken, a clean list looks like a menu. Each section speaks with its own
 * call to action ("See the menu", "Shop merch").
 */
export function EventAddonsShowcase() {
  const addons = useEventAddons();
  const now = useNow(() => 60_000);
  if (!addons) return null;
  const { menu } = addons;
  const session = focusSession(menu, addons.previewOccurrenceId);
  const ordered = orderedItems(menu);

  return (
    <div className="space-y-8">
      {ordered.count && ordered.occurrenceId ? (
        <Link
          href={`/wallet/preorders/${ordered.occurrenceId}`}
          className="flex items-center gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/8 p-4"
        >
          <CheckCircle2
            className="size-6 shrink-0 text-emerald-600 dark:text-emerald-400"
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-text-primary dark:text-white">
              You've pre-ordered {ordered.count} item
              {ordered.count === 1 ? "" : "s"}
            </span>
            <span className="block text-sm text-text-secondary dark:text-white/60">
              Your pickup passes are in your wallet
            </span>
          </span>
          <ChevronRight
            className="size-5 shrink-0 text-text-tertiary"
            aria-hidden
          />
        </Link>
      ) : null}
      {menu.sections.map((section) => {
        const withImages = section.items.filter((item) => item.image).length;
        const rail = withImages >= Math.ceil(section.items.length / 2);
        const cutoffIso = session?.cutoffs[section.id] ?? null;
        const open = () =>
          addons.open({
            occurrenceId: session?.occurrenceId ?? null,
            sectionId: section.id,
          });
        // A branded section renders as the partner's own panel. Its colours
        // are resolved on the server and inlined, so the first paint is
        // already on-brand — nothing restyles after hydration.
        const brand = section.brand;
        const tone = (branded: string, plain: string) =>
          brand ? branded : plain;
        return (
          <section
            key={section.id}
            className={cn(
              "space-y-3",
              brand && "rounded-[26px] border border-[var(--brand-border)] p-4",
            )}
            style={brand ? brandThemeStyle(brand) : undefined}
          >
            {brand ? (
              <BrandMark
                brand={brand}
                name={section.partnerName || section.title}
                size="lg"
              />
            ) : null}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3
                  className={cn(
                    "text-xl font-bold",
                    tone(
                      "text-[var(--brand-ink)]",
                      "text-text-primary dark:text-white",
                    ),
                  )}
                >
                  {section.title}
                </h3>
                <p
                  className={cn(
                    "mt-0.5 text-sm",
                    tone(
                      "text-[var(--brand-ink-muted)]",
                      "text-text-secondary dark:text-white/60",
                    ),
                  )}
                >
                  {section.partnerName && !brand ? (
                    <>
                      by{" "}
                      <span className="font-medium text-text-primary dark:text-white">
                        {section.partnerName}
                      </span>{" "}
                      ·{" "}
                    </>
                  ) : null}
                  {section.requiresTicket
                    ? "For ticket holders"
                    : "Optional · collected at the venue"}
                </p>
              </div>
              <button
                type="button"
                onClick={open}
                className={cn(
                  "inline-flex shrink-0 items-center gap-0.5 pt-1 text-sm font-semibold",
                  tone(
                    "text-[var(--brand-accent-text)]",
                    "text-text-primary dark:text-white",
                  ),
                )}
              >
                {section.actionLabel}
                <ChevronRight className="size-4" aria-hidden />
              </button>
            </div>

            {section.description ? (
              <p
                className={cn(
                  "line-clamp-3 text-sm leading-relaxed whitespace-pre-line",
                  tone(
                    "text-[var(--brand-ink-muted)]",
                    "text-text-secondary dark:text-white/65",
                  ),
                )}
              >
                {section.description}
              </p>
            ) : null}

            {rail ? (
              <div className="-mx-3 flex snap-x scroll-px-3 [scrollbar-width:none] items-start gap-3 overflow-x-auto px-3 pb-1">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={open}
                    className="flex w-[132px] shrink-0 snap-start flex-col text-left"
                  >
                    {item.image ? (
                      <AddonThumb
                        image={item.image}
                        alt={item.name}
                        sizes="132px"
                        className="aspect-square w-full rounded-2xl"
                      />
                    ) : (
                      <span
                        className={cn(
                          "flex aspect-square w-full items-center justify-center rounded-2xl p-3 text-center text-sm leading-snug font-semibold",
                          tone(
                            "bg-[var(--brand-raised)] text-[var(--brand-accent-text)]",
                            "bg-brand/8 text-brand dark:bg-white/6 dark:text-white/80",
                          ),
                        )}
                      >
                        {item.name}
                      </span>
                    )}
                    <span
                      className={cn(
                        "mt-2 line-clamp-2 text-[13px] leading-snug font-semibold",
                        tone(
                          "text-[var(--brand-ink)]",
                          "text-text-primary dark:text-white",
                        ),
                      )}
                    >
                      {item.name}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 block text-[13px] tabular-nums",
                        tone(
                          "text-[var(--brand-accent-text)]",
                          "text-text-secondary dark:text-white/60",
                        ),
                      )}
                    >
                      {formatPrice(item.priceMinor, item.currency)}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <button
                type="button"
                onClick={open}
                className={cn(
                  "block w-full overflow-hidden rounded-2xl border text-left",
                  tone(
                    "border-[var(--brand-border)] bg-[var(--brand-raised)]",
                    "border-black/8 bg-surface dark:border-white/10 dark:bg-white/[0.03]",
                  ),
                )}
              >
                {section.items.slice(0, 4).map((item) => (
                  <span
                    key={item.id}
                    className={cn(
                      "flex items-baseline gap-3 border-b px-4 py-3 last:border-b-0",
                      tone(
                        "border-[var(--brand-border)]",
                        "border-black/6 dark:border-white/8",
                      ),
                    )}
                  >
                    <span
                      className={cn(
                        "min-w-0 flex-1 truncate text-sm font-medium",
                        tone(
                          "text-[var(--brand-ink)]",
                          "text-text-primary dark:text-white",
                        ),
                      )}
                    >
                      {item.name}
                    </span>
                    <span
                      className={cn(
                        "shrink-0 text-sm tabular-nums",
                        tone(
                          "font-semibold text-[var(--brand-accent-text)]",
                          "text-text-secondary dark:text-white/60",
                        ),
                      )}
                    >
                      {formatPrice(item.priceMinor, item.currency)}
                    </span>
                  </span>
                ))}
                {section.items.length > 4 ? (
                  <span
                    className={cn(
                      "flex items-center justify-between px-4 py-3 text-sm font-semibold",
                      tone(
                        "text-[var(--brand-accent-text)]",
                        "text-brand dark:text-white",
                      ),
                    )}
                  >
                    {section.actionLabel} · {section.items.length} items
                    <ChevronRight className="size-4" aria-hidden />
                  </span>
                ) : null}
              </button>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              {cutoffIso ? (
                <AddonDeadline
                  cutoffIso={cutoffIso}
                  now={now}
                  branded={Boolean(brand)}
                />
              ) : (
                <span
                  className={cn(
                    "text-xs",
                    tone(
                      "text-[var(--brand-ink-muted)]",
                      "text-text-secondary dark:text-white/55",
                    ),
                  )}
                >
                  {addonDeadlineRule(section.cutoffHours)}
                </span>
              )}
              <button
                type="button"
                onClick={open}
                className={cn(
                  "rounded-xl px-4 py-2 text-sm font-semibold transition-transform active:scale-[0.98]",
                  brand
                    ? session?.canOrder
                      ? "bg-[var(--brand-accent)] text-[var(--brand-accent-ink)]"
                      : "bg-[var(--brand-raised)] text-[var(--brand-ink)]"
                    : session?.canOrder
                      ? "bg-brand text-white"
                      : "bg-black/5 text-text-primary dark:bg-white/10 dark:text-white",
                )}
              >
                {session?.canOrder ? "Pre-order" : section.actionLabel}
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );
}
